// 特征删除 / 撤销 —— 画布与特征表共用的一份逻辑。
// ------------------------------------------------------------------
// 为什么抽出来：删除有两个入口（画布上选中后按 Delete、表格每行的「删除」按钮，
// 外加表格的多选批量删除），撤销也必须两边都能用。若各自实现一遍，
// 提示措辞、刷新范围、撤销深度提示迟早会漂移 —— 用户会以为它们不是同一个操作。
//
// 交互约定（2026-09-16 用户要求）：**按了就删，不弹二次确认**。
// 取而代之的是删除后提示条上的「撤销」按钮（8 秒可见）+ 全局 Ctrl+Z。
// 撤销由后端保证「回到原编号位置」（见 backend/services/undo_service.py）。
import { useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import {
  batchDeleteFeatures, deleteFeature, undoDeleteFeature,
} from '../api/client';
import type { UndoResult } from '../api/client';
import { useUI } from '../store/ui';

/**
 * 撤销结束后的统一播报。
 *
 * 单独抽出来是因为它有两个调用方（组件内的 useFeatureDelete、App 级的
 * useUndoDelete），而「栈空」这件事必须区别对待：用户按 Ctrl+Z 常常只是习惯
 * 动作，此时用红色报错条去打断他，比什么都不说更烦。
 */
function reportUndo(res: UndoResult, showToast: ReturnType<typeof useUI.getState>['showToast']) {
  if (!res.restored.length) {
    showToast(`撤销失败：${res.failed[0]?.error ?? '没有可恢复的内容'}`, 'error');
    return;
  }
  const nos = res.restored.map((r) => `#${r.balloon_no}`).join('、');
  const more = res.remaining > 0 ? `（还可撤销 ${res.remaining} 次）` : '';
  showToast(`已恢复 ${nos}${more}`, 'success');
}

function reportUndoError(e: any, showToast: ReturnType<typeof useUI.getState>['showToast']) {
  // 404 = 撤销栈里没有东西了。这是正常状态，不该按错误处理。
  const empty = e?.response?.status === 404;
  if (empty) {
    showToast(String(e?.message || '没有可撤销的删除'), 'info');
  } else {
    showToast(`撤销失败：${e?.message ?? '未知错误'}`, 'error');
  }
}

/** 刷新三处受编号重排影响的数据。 */
function refreshAfterChange(qc: QueryClient, drawingId: number) {
  qc.invalidateQueries({ queryKey: ['drawing', drawingId] });
  qc.invalidateQueries({ queryKey: ['drawings'] });
  qc.invalidateQueries({ queryKey: ['balloons', drawingId] });
}

/**
 * @param drawingId 当前图纸
 * @param onDeleted 可选：删除成功后回调被删特征的主键（用于收起详情面板等）
 */
export function useFeatureDelete(drawingId: number,
                                 onDeleted?: (featurePk: number) => void) {
  const qc = useQueryClient();
  const showToast = useUI((s) => s.showToast);
  // 回调存 ref：调用方多半写内联箭头函数，直接进依赖会让 mutation 每次重渲染重建
  const cbRef = useRef(onDeleted);
  cbRef.current = onDeleted;

  // 删除/撤销都会改动编号 → 表格、画布气球、图纸列表三处都要按新数据重渲染
  const refresh = useCallback(() => {
    refreshAfterChange(qc, drawingId);
  }, [qc, drawingId]);

  // ---------- 撤销（定义在前，供下面两个删除的提示按钮引用） ----------
  const undoMut = useMutation({
    mutationFn: () => undoDeleteFeature(drawingId),
    onSuccess: (res) => { refresh(); reportUndo(res, showToast); },
    onError: (e: any) => reportUndoError(e, showToast),
  });
  const undoNow = undoMut.mutate;

  const undoAction = useCallback(
    () => ({ label: '撤销', run: () => undoNow() }), [undoNow]);

  // ---------- 单个删除 ----------
  const delMut = useMutation({
    mutationFn: (pk: number) => deleteFeature(drawingId, pk),
    onSuccess: (snap) => {
      cbRef.current?.(snap.id);
      refresh();
      showToast(`已删除 #${snap.balloon_no}，编号已补位`, 'success', undoAction());
    },
    onError: (e: any) => showToast(`删除失败：${e.message}`, 'error'),
  });

  // ---------- 批量删除（表格多选） ----------
  const batchDelMut = useMutation({
    mutationFn: (pks: number[]) => batchDeleteFeatures(drawingId, pks),
    onSuccess: (res) => {
      res.deleted_items.forEach((i) => cbRef.current?.(i.id));
      refresh();
      const miss = res.missing.length ? `（跳过 ${res.missing.length} 个已失效项）` : '';
      showToast(`已删除 ${res.deleted} 项${miss}`, 'success', undoAction());
    },
    onError: (e: any) => showToast(`批量删除失败：${e.message}`, 'error'),
  });

  return {
    remove: delMut.mutate,
    removeMany: batchDelMut.mutate,
    undo: undoNow,
    // 删除/撤销都会改动编号 → 调用方据此禁用按钮，避免连点
    busy: delMut.isPending || batchDelMut.isPending || undoMut.isPending,
    undoPending: undoMut.isPending,
  };
}

/** 只想要撤销能力时用它（例：全局 Ctrl+Z）。 */
export function useUndoDelete(drawingId: number | null) {
  const qc = useQueryClient();
  const showToast = useUI((s) => s.showToast);

  const { mutate, isPending } = useMutation({
    mutationFn: () => undoDeleteFeature(drawingId as number),
    onSuccess: (res) => {
      refreshAfterChange(qc, drawingId as number);
      reportUndo(res, showToast);
    },
    onError: (e: any) => reportUndoError(e, showToast),
  });

  return {
    undo: useCallback(() => { if (drawingId != null) mutate(); },
                      [drawingId, mutate]),
    undoPending: isPending,
  };
}
